import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import {
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import {
  patchGenerarDeclaracion,
  postDeclaracionHospedaje,
  postRechazarDeclaracion,
} from 'api/recaudacion';
import { Button, Text } from 'components';
import Select from 'components/PaginatedSelect';
import propTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { actualizacionTotal, getINPC, recargosTotal } from 'utils/autocalculos';
import { DATE_FORMAT } from 'utils/formatters';
import _logger from 'utils/logger';
import { toMoment } from 'utils/normalizers';
import { CURP_PATTERN, RFC_PATTERN } from 'utils/patterns';
import round from 'utils/round';
import ModalConfirm from './ModalConfirm';

const _rules = {
  required: (message) => ({
    required: true,
    message,
  }),
  numeric: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+([.][0-9]{1,2})?$/;
        if (!value) return Promise.resolve();
        if (!Number.isInteger(parseFloat(value))) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  mayorCero: [
    {
      validator: async (_, value) => {
        if (value <= 0) {
          throw new Error('El valor debe ser mayor a 0.');
        }
      },
    },
  ],
  entero: [
    {
      validator: async (_, value) => {
        const regex = /^[0-9]+$/;
        if (!value) return Promise.resolve();
        if (!regex.test(value)) {
          return Promise.reject(new Error('Solo se permiten números enteros positivos.'));
        }
        return Promise.resolve();
      },
    },
  ],
  rfc: [{ pattern: RFC_PATTERN, message: ' Ingrese un RFC válido.' }],
  curp: [{ pattern: CURP_PATTERN, message: ' Ingrese una CURP válida.' }],
};

const initialValues = {
  es_declaracion_en_ceros: false,
  retencion: 0,
  actualizacion: 0,
  recargos: 0,
  multas: 0,
  cantidad_a_cargo: 0,
  cantidad_a_favor: 0,
  total_de_contribuciones: 0,
  impuesto: 0,
  impuesto_determinado: 0,
  impuesto_trasladado: 0,
  monto_pagado_con_anterioridad: 0,
  base_de_impuesto: 0,
};

const calcularImpuestoTrasladado = (baseImpuesto, porcentajePeriodo) => {
  if (!baseImpuesto) return 1 * porcentajePeriodo;
  return round(parseFloat(baseImpuesto) * porcentajePeriodo || 0)[0];
};

const calcularImpuestoDeterminado = (impuestoTrasladado, retencion) => {
  const _impuestoTrasladado = parseFloat(impuestoTrasladado) || 0;
  const _retencion = parseFloat(retencion) || 0;
  return round(_impuestoTrasladado - _retencion)[0];
};

const calcularTotalContribuciones = (impuestoDeterminado, actualizacion, recargos, multas) => {
  const total = [
    impuestoDeterminado,
    actualizacion,
    recargos,
    multas,
  ].reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
  return round(total || 0)[0];
};

const calcularCantidadACargo = (totalContribuciones, montoPagadoAnterioridad) => {
  const _montoPagado = parseFloat(montoPagadoAnterioridad) || 0;
  const _totalContribuciones = parseFloat(totalContribuciones) || 0;
  const _cantidadACargo = _totalContribuciones - _montoPagado;

  if (_cantidadACargo > 0) {
    return {
      cantidadACargo: round(_cantidadACargo)[0],
      cantidadAFavor: 0,
    };
  }

  return {
    cantidadACargo: 0,
    cantidadAFavor: round((_montoPagado * 1) - _totalContribuciones)[0],
  };
};

const FormHospedaje = ({
  padron,
  statusDeclaracion,
  visible,
  setVisibleForm,
  isComplementaria,
  cargosGenerados,
  callback,
  callbackCancel,
  declaracionId,
}) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [isZeroState, setIsZeroState] = useState(false);
  const [porcentajePeriodo, setPorcentajePeriodo] = useState(0);
  const [INPC, setINPC] = useState(0);

  // watchers
  const _impuesto_determinado = Form.useWatch('impuesto_determinado', form);// A
  const actualizacion = Form.useWatch(['actualizacion'], form); // B
  const recargos = Form.useWatch('recargos', form); // C
  const multas = Form.useWatch('multas', form); // D
  const totalDeContribuciones = Form.useWatch('total_de_contribuciones', form); // E
  const montoPagadoConAnterioridad = Form.useWatch('monto_pagado_con_anterioridad', form); // F

  const handleCancel = async () => {
    setLoading(true);
    await postRechazarDeclaracion({
      declaracion: declaracionId,
      canal_de_pago: 5,
    });
    setLoading(false);
  };

  const handleUnload = (event) => {
    const message = 'Se perderán los datos capturados, ¿Estás seguro de salir?';
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.returnValue = message; // Esto mostrará el cuadro de confirmación
    return message;
  };

  const calcularActualizacion = async ({ importe }) => {
    const _actualizacion = await actualizacionTotal({
      importe_cargo: importe,
      tipo_de_cargo: cargosGenerados[0],
      INPC,
    });
    form.setFieldsValue({ actualizacion: round(_actualizacion)[0] });
  };

  const calcularRecargos = async ({ importe }) => {
    const base_gravada = typeof _impuesto_determinado === 'string'
      ? parseFloat(_impuesto_determinado)
      : _impuesto_determinado;
    const _recargos = await recargosTotal({
      tipo_de_cargo: cargosGenerados[0],
      importe_cargo: importe,
      fecha_de_vencimiento: cargosGenerados[0]?.fecha_de_vencimiento,
      base_gravada,
    });
    form.setFieldsValue({ recargos: round(_recargos || 0)[0] });
  };

  // calculamos la actualizacion en base al impuesto determinado
  useEffect(() => {
    calcularActualizacion({ importe: _impuesto_determinado });
  }, [_impuesto_determinado]);

  // calculamos los recargos en base a la actualizacion
  useEffect(() => {
    calcularRecargos({ importe: actualizacion });
  }, [actualizacion, _impuesto_determinado]);

  // calculamos el total de contribuciones
  useEffect(() => {
    const toNum = (v) => parseFloat(v) || 0;
    const total = [toNum(_impuesto_determinado),
      toNum(actualizacion),
      toNum(recargos),
      toNum(multas)]
      .reduce((a, b) => a + b, 0);

    form.setFieldsValue({
      total_de_contribuciones: round(total)[0],
    });
  }, [_impuesto_determinado, actualizacion, recargos, multas]);

  // Calculamos cantidad_a_cargo (E - G)
  // A la cantidad a cargo se le quita el monto pagado con anterioridad
  // Si el resultado es positivo, se muestra la cantidad a cargo
  // Si el resultado es negativo, se muestra la cantidad a favor
  useEffect(() => {
    const _montoPagadoConAnterioridad = parseFloat(montoPagadoConAnterioridad) || 0;
    const _totalDeContribuciones = parseFloat(totalDeContribuciones) || 0;
    const _cantidadACargo = _totalDeContribuciones - _montoPagadoConAnterioridad;
    if (_cantidadACargo > 0) {
      form.setFieldsValue({
        cantidad_a_cargo: round(_cantidadACargo || 0)[0],
        cantidad_a_favor: 0,
      });
    } else {
      form.setFieldsValue({
        cantidad_a_favor: round((_montoPagadoConAnterioridad * 1) - _totalDeContribuciones)[0],
        cantidad_a_cargo: 0,
      });
    }
  }, [montoPagadoConAnterioridad, totalDeContribuciones]);

  const calculateINPC = async () => {
    if (cargosGenerados.length > 0) {
      const periodo = cargosGenerados[0]?.periodo_fiscal?.periodo || '2024';
      const mes = cargosGenerados[0]?.identificador_periodo;
      const _inpc = await getINPC(periodo, mes);
      setINPC(_inpc?.[0] ?? {});
    }
  };

  useEffect(() => {
    if (visible) {
      calculateINPC();
    }
    window.addEventListener('unload', handleUnload);
    window.addEventListener('beforeunload', handleCancel);

    return () => {
      window.removeEventListener('unload', handleUnload);
      window.removeEventListener('beforeunload', handleCancel);
    };
  }, [visible]);

  // Resetea el formulario cuando se cierra.
  useEffect(() => {
    if (visible) {
      const _porcentaje = ((cargosGenerados[0]?.importe || 5) / 100);
      setPorcentajePeriodo(_porcentaje);
      form.resetFields();
    }
  }, [visible]);

  const patchDeclaracionStatus = async (values) => {
    try {
      let statusId = null;

      const statusObject = {
        generada: statusDeclaracion.find((status) => !status.declaracion_presentada
          && !status.declaracion_en_ceros)?.id,
        en_ceros: statusDeclaracion.find((status) => status.declaracion_en_ceros)?.id,
        sin_importe: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidades a Pagar y a Favor en Cero')?.id || 0,
        saldo_favor: statusDeclaracion.find((status) => status.descripcion === 'Declaración con Cantidad a Favor')?.id || 0,
      };

      if (isZeroState) {
        statusId = statusObject.en_ceros;
      } else if (parseFloat(values.cantidad_a_favor) > 0) {
        statusId = statusObject.saldo_favor;
      } else if (parseFloat(values.cantidad_a_cargo) <= 0) {
        statusId = statusObject.sin_importe;
      } else {
        statusId = statusObject.generada;
      }

      const request = await patchGenerarDeclaracion({
        id: values.declaracion_empresarial,
        estatus_de_declaracion_empresarial: statusId,
        razones_para_presentar_en_ceros: values.razones_para_presentar_en_ceros,
        numero_de_habitaciones: values.numero_de_habitaciones,
      });

      if (!request) throw new Error('Error al actualizar la declaración.');
    } catch (error) {
      _logger(error);
    }
  };

  // Envia los datos del formulario.
  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const _padronValues = {
        razon_social_nombre_completo:
        padron?.empresa?.contribuyente?.razon_social
        || padron?.empresa?.contribuyente?.nombre_completo
        || '',
        rfc: padron?.empresa?.contribuyente?.rfc || '',
        curp: padron?.empresa?.contribuyente?.curp || '',
        nombre: padron?.empresa?.contribuyente?.nombre_completo || '',
        nombre_completo: padron?.empresa?.contribuyente?.nombre_completo || '',
        apellido_paterno: padron?.empresa?.contribuyente?.apellido_paterno || '',
        apellido_materno: padron?.empresa?.contribuyente?.apellido_materno || '',
      };

      let _values = {
        ...values,
        ..._padronValues,
        declaracion_empresarial: declaracionId,
        numero_de_habitaciones: values.numero_de_habitaciones === '' ? 0 : values.numero_de_habitaciones,
        entidad: 1,
        fecha_del_pago_realizado_anterior: isComplementaria
          && !isZeroState ? values.fecha_del_pago_realizado_anterior.format(DATE_FORMAT) : null,
      };

      if (isZeroState) {
        _values = {
          ..._values,
          actualizacion: 0,
          cantidad_a_favor: 0,
          cantidad_a_cargo: 0,
          impuesto: 0,
          impuesto_determinado: 0,
          impuesto_trasladado: 0,
          base_de_impuesto: 0,
          monto_pagado_con_anterioridad: 0,
          multas: 0,
          recargos: 0,
          retencion: 0,
          total_de_contribuciones: 0,
        };
      }

      if (!isComplementaria) {
        _values = {
          ..._values,
          fecha_del_pago_realizado_anterior: null,
          monto_pagado_con_anterioridad: 0,
        };
      }

      await patchDeclaracionStatus(_values);
      await postDeclaracionHospedaje(_values);

      callback(_values.cantidad_a_cargo <= 0);
      setIsZeroState(false);
      setIsVisibleModal(false);
      setVisibleForm(false);
    } catch (error) {
      _logger(error);
    } finally {
      setLoading(false);
    }
  };

  const validacionesDeOperaciones = (values) => {
    if (parseFloat(values.impuesto_determinado) < 0) {
      form.validateFields(['impuesto_determinado']).then(() => {
        form.setFields([
          {
            name: 'impuesto_determinado',
            errors: ['El valor no puede ser negativo.'],
          },
        ]);
      });
    }
  };

  const total = () => {
    const values = form.getFieldsValue();
    const impuestoTrasladado = calcularImpuestoTrasladado(
      values.base_de_impuesto,
      porcentajePeriodo,
    );

    const impuestoDeterminado = calcularImpuestoDeterminado(
      impuestoTrasladado,
      values.retencion,
    );

    const totalContribuciones = calcularTotalContribuciones(
      impuestoDeterminado,
      values.actualizacion,
      values.recargos,
      values.multas,
    );

    const { cantidadACargo, cantidadAFavor } = calcularCantidadACargo(
      totalContribuciones,
      values.monto_pagado_con_anterioridad,
    );

    form.setFieldsValue({
      impuesto_trasladado: impuestoTrasladado,
      impuesto_determinado: impuestoDeterminado,
      total_de_contribuciones: totalContribuciones,
      cantidad_a_cargo: cantidadACargo,
      cantidad_a_favor: cantidadAFavor,
    });

    validacionesDeOperaciones({
      base_de_impuesto: values.base_de_impuesto,
      impuesto_trasladado: impuestoTrasladado,
      impuesto_determinado: impuestoDeterminado,
    });
  };

  return (
    <>
      <Modal
        centered
        title={(
          <Row justify="space-between">
            <Text $accent>
              IMPUESTO SOBRE HOSPEDAJE
            </Text>
          </Row>
        )}
        visible={visible}
        closable={false}
        footer={(
          <Row justify="space-between" gutter={[10, 15]}>
            <Col xs={24} sm={24} md={12} style={{ textAlign: 'left' }}>
              <Text $accent>* Este campo es obligatorio</Text>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => callbackCancel()}
                loading={loading}
                $accent
                icon={<CloseOutlined />}
              >
                <Text $accent>Cancelar</Text>
              </Button>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Button
                type="primary"
                style={{ width: '100%', borderRadius: 5 }}
                onClick={() => {
                  form.validateFields().then(() => {
                    setIsVisibleModal(true);
                  }).catch(() => {
                    setIsVisibleModal(false);
                  });
                }}
                loading={loading}
                icon={<SyncOutlined />}
                $accent
              >
                <Text>Generar</Text>
              </Button>
            </Col>
          </Row>
        )}
        width="90vw"
        style={{ maxWidth: '1200px' }}
      >
        <Spin spinning={loading} tip="Cargando...">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={initialValues}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  name="es_declaracion_en_ceros"
                  label="¿Es Declaración en ceros?"
                  required
                >
                  <Select
                    placeholder="Selecciona una opción"
                    trueFalse
                    allowClear={false}
                    onChange={(value) => setIsZeroState(value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12} lg={8}>
                <Form.Item
                  hasFeedback
                  name="numero_de_habitaciones"
                  rules={[..._rules.numeric]}
                  label="Total de habitaciones"
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              {isZeroState ? (
                <Col xs={24}>
                  <Form.Item
                    name="razones_para_presentar_en_ceros"
                    label="Razones por las que presenta en ceros"
                    rules={[_rules.required('El campo es requerido')]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>
                </Col>
              ) : (
                <Col xs={24}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Concepto</th>
                          <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>A. Impuesto base</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="base_de_impuesto"
                              tooltip="Suma de los ingresos directos e indirectos obtenidos por el 5% del impuesto al hospedaje."
                              rules={[
                                _rules.required('El campo es requerido'),
                                ..._rules.numeric,
                                ..._rules.mayorCero,
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input
                                prefix="$"
                                onChange={(value) => {
                                  if (value.target.value === '') {
                                    form.setFieldsValue({
                                      impuesto_trasladado: 1 * porcentajePeriodo,
                                    });
                                  } else {
                                    const val_monto = parseFloat(value.target.value)
                                      * porcentajePeriodo;
                                    form.setFieldsValue({
                                      impuesto_trasladado: val_monto.toFixed(2),
                                    });
                                  }
                                  total();
                                }}
                              />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>B. Impuesto trasladado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="impuesto_trasladado"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>C. Retención del impuesto al hospedaje</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="Retención del impuesto al hospedaje por intermediarios, promotor o facilitador y/o por medio de plataformas digitales"
                              name="retencion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>D. Impuesto determinado</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="(A - B) o en su caso solo A"
                              name="impuesto_determinado"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>E. Actualización</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="actualizacion"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>F. Recargos</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="recargos"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>G. Multas</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              name="multas"
                              rules={[_rules.required('El valor tiene que ser 0 o mayor que 0'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" onChange={() => total()} />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>H. Total de contribuciones</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              hasFeedback
                              tooltip="(C + D + E + F) o en su caso solo C"
                              name="total_de_contribuciones"
                              rules={[_rules.required('El campo es requerido'), ..._rules.numeric]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        {isComplementaria && (
                          <>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>I. Fecha del pago anterior</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  name="fecha_del_pago_realizado_anterior"
                                  rules={[_rules.required('El campo es requerido')]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <DatePicker
                                    format={DATE_FORMAT}
                                    disabledDate={(d) => d.isAfter(toMoment(new Date()))}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>J. Monto pagado con anterioridad</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                                <Form.Item
                                  hasFeedback
                                  name="monto_pagado_con_anterioridad"
                                  rules={[
                                    _rules.required('El valor tiene que ser 0 o mayor que 0'),
                                    ..._rules.numeric,
                                  ]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input prefix="$" onChange={() => total()} />
                                </Form.Item>
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>K. Cantidad a pagar</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_cargo"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>L. Cantidad a favor</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Form.Item
                              name="cantidad_a_favor"
                              style={{ marginBottom: 0 }}
                            >
                              <Input prefix="$" disabled />
                            </Form.Item>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Col>
              )}
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalConfirm
        visible={isVisibleModal}
        setVisible={setIsVisibleModal}
        onFinish={() => onFinish()}
        body="Al generar la declaración no podrá ser modificada. ¿Desea continuar?"
        btnText="Si, generar declaración"
        loading={loading}
      />
    </>
  );
};

FormHospedaje.propTypes = {
  padron: propTypes.shape({
    empresa: propTypes.shape({
      contribuyente: propTypes.shape({
        razon_social: propTypes.string,
        rfc: propTypes.string,
        nombre_completo: propTypes.string,
        curp: propTypes.string,
        nombre: propTypes.string,
        apellido_paterno: propTypes.string,
        apellido_materno: propTypes.string,
        ciudadano: propTypes.shape({
          curp: propTypes.string,
          nombre: propTypes.string,
          apellido_paterno: propTypes.string,
          apellido_materno: propTypes.string,
        }),
      }),
    }),
  }).isRequired,
  statusDeclaracion: propTypes.number.isRequired,
  visible: propTypes.bool,
  isComplementaria: propTypes.bool,
  setVisibleForm: propTypes.func.isRequired,
  cargosGenerados: propTypes.arrayOf(propTypes.shape({
    periodo_fiscal: propTypes.shape({
      periodo: propTypes.string,
    }),
    identificador_periodo: propTypes.string,
    importe: propTypes.number,
    fecha_de_vencimiento: propTypes.string,
  })),
  callback: propTypes.func.isRequired,
  callbackCancel: propTypes.func.isRequired,
  declaracionId: propTypes.func.isRequired,
};

FormHospedaje.defaultProps = {
  isComplementaria: false,
  cargosGenerados: [],
  visible: false,
};

export default FormHospedaje;
